import { useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import FlatPickr from "react-flatpickr";
import { BadgePercent, BarChart3, Download, Receipt, RotateCcw, Search, Wallet } from "lucide-react";
import moment from "moment";
import { getAllVenueBySlug, getReport, downloadTicketByBookingNo } from "../../../api";
import ExportToExcel from "../../../components/ExportToExcel/ExportToExcel";
import FormattedDate from "../../../components/common/DateFormat/FormattedDate";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import CommonSelect from "../../../components/common/Select/CommonSelect";
import { handleError } from "../../../utils/errorHandler";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import { PulseLoader } from "react-spinners";

/** Revenue Report — confirmed primary bookings; shell matches Attendee Report v3. */
const EventRevenueReport = () => {
  const { isNightMode } = useNightMode();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fullBookingList, setFullBookingList] = useState<any[]>([]);
  // Table always uses raw bookings (Contact / TicketType), never generateCount summary rows
  const [displayRows, setDisplayRows] = useState<any[]>([]);
  const [selectedVenue, setSelectedVenue] = useState("0");
  const [eventVenues, setEventVenues] = useState<any[]>([]);
  const [downloadingTicket, setDownloadingTicket] = useState(false);
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [reports, setReports] = useState<{ title: string; description: string | number }[]>([
    { title: "Organizer Revenue", description: 0 },
    { title: "Total Discount", description: 0 },
    { title: "Total Fee", description: 0 },
  ]);

  const getEventVenue = async (slugId: string) => {
    try {
      const resp = await getAllVenueBySlug(slugId);
      const venueOptions = (resp || []).map((item: any) => ({
        value: String(item.venueId),
        label: item.venueName,
      }));
      setEventVenues([{ value: "0", label: "All Venues" }, ...venueOptions]);
    } catch (error) {
      handleError(error);
    }
  };

  // Stats only — never overwrite displayRows / table data
  const generateCount = (data: any[]) => {
    const eventData = localStorage.getItem("eventData");
    if (!eventData) return;
    const { ticketFee, processingFee } = JSON.parse(eventData);
    let totalEventoxFee = 0;
    let totalStripeFee = 0;
    let totalDiscountAmount = 0;
    let totalOrganizerRevenue = 0;
    // processingFee + ticketFee both 0 → fees collected from organizer
    const feeCollectedFromOrganizer = processingFee === 0 && ticketFee === 0;

    data.forEach((item) => {
      if (item.bookedBy !== "0" || item.bookingStatus !== "confirmed") return;
      let stripefee = 0;
      let eventoxRevenue = 0;
      let organizerRevenue = 0;
      const grandTotal = item.grandTotal;
      const ticketAmount = item.totalTicketAmount;
      const discountAmount = item.discountAmount ? item.discountAmount : 0;
      const ticketCount = item.totalTicketCount;

      if (feeCollectedFromOrganizer) {
        stripefee = (grandTotal / 100) * 2.7 + 0.3;
        const ourFee = (grandTotal / 100) * 2.5 + ticketCount * 0.5;
        eventoxRevenue = Number(ticketCount) === 1 ? 0 : Number(ourFee) - Number(stripefee);
        organizerRevenue = grandTotal - stripefee - eventoxRevenue - discountAmount;
      } else {
        stripefee =
          item.stripeRevenue === null ? (grandTotal / 100) * 2.7 + 0.3 : item.stripeRevenue;
        eventoxRevenue =
          item.eventoxRevenue === null
            ? grandTotal - stripefee - ticketAmount
            : item.eventoxRevenue;
        organizerRevenue =
          item.organizerRevenue === null
            ? ticketAmount - stripefee - eventoxRevenue - discountAmount
            : item.organizerRevenue;
      }

      totalStripeFee += Number(stripefee);
      totalEventoxFee += Number(eventoxRevenue);
      totalDiscountAmount += Number(discountAmount);
      totalOrganizerRevenue += Number(organizerRevenue);
    });

    const currency = data[0]?.currency || "$";
    const totalFee = totalStripeFee + totalEventoxFee;
    setReports([
      { title: "Organizer Revenue", description: `${currency} ${totalOrganizerRevenue.toFixed(2)}` },
      { title: "Total Discount", description: `${currency} ${totalDiscountAmount.toFixed(2)}` },
      { title: "Total Fee", description: `${currency} ${totalFee.toFixed(2)}` },
    ]);
  };

  const generateExportData = (data: any[]) =>
    data.map((item: any) => ({
      userName: item?.Contact?.firstName || "",
      email: item?.Contact?.email || "",
      phoneNo: item?.Contact?.phoneNo || "",
      ticketType: item?.TicketType?.ticketType || "",
      totalTicketCount: item?.totalTicketCount || "",
      ticketAmount: item?.totalTicketAmount || "",
      discountAmount: item?.discountAmount || "",
      fee: Number(item.stripeRevenue || 0) + Number(item.eventoxRevenue || 0),
      grandTotal: item?.grandTotal || "",
      createdAt: item?.createdAt || "",
    }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const eventData = localStorage.getItem("eventData");
        if (!eventData) return;
        const { eventId: eventSlug, slugId } = JSON.parse(eventData);
        const [response] = await Promise.all([getReport(eventSlug), getEventVenue(slugId)]);
        if (!response) return;
        const mainBooking = response.filter(
          (item: any) => item.bookingStatus === "confirmed" && item.bookedBy === "0"
        );
        setFullBookingList(mainBooking);
        setDisplayRows(mainBooking);
        generateCount(mainBooking);
      } catch (error) {
        console.error("Error fetching organizer revenue:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const onSelectedVenue = async (venueId: string) => {
    setIsLoading(true);
    try {
      const eventData = localStorage.getItem("eventData");
      if (!eventData) return;
      const eventSlug = JSON.parse(eventData).eventId;
      const response = await getReport(eventSlug);
      const active = response.filter((item: any) => item.bookingStatus === "confirmed");
      const mainBooking =
        venueId !== "0"
          ? active.filter((item: any) => item.bookedBy === "0" && String(item.venueId) === venueId)
          : active.filter((item: any) => item.bookedBy === "0");
      setFullBookingList(mainBooking);
      setDisplayRows(mainBooking);
      generateCount(mainBooking);
      setSelectedVenue(venueId);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter full list → displayRows; regenerate stats from filtered raw bookings
  useEffect(() => {
    const qName = search.trim().toLowerCase();
    const filtered = fullBookingList.filter((item: any) => {
      const first = item?.Contact?.firstName || "";
      const last = item?.Contact?.lastName || "";
      const name = `${first} ${last}`.trim().toLowerCase();
      const bookingDate = moment(item?.createdAt);
      const inRange =
        (!fromDate || bookingDate.isSameOrAfter(moment(fromDate).startOf("day"))) &&
        (!toDate || bookingDate.isSameOrBefore(moment(toDate).endOf("day")));
      return (!qName || name.includes(qName)) && inRange;
    });
    setDisplayRows(filtered);
    generateCount(filtered);
  }, [search, fullBookingList, fromDate, toDate]);

  const clearFilters = () => {
    setSearch("");
    setFromDate(undefined);
    setToDate(undefined);
    if (selectedVenue !== "0") onSelectedVenue("0");
  };

  const hasActiveFilters = Boolean(search || fromDate || toDate) || selectedVenue !== "0";

  const handleDownloadTicket = async (row: any) => {
    try {
      setDownloadingTicket(true);
      const bookingNo = row?.primaryBookingNo || row?.bookingNo;
      if (!bookingNo) throw new Error("Booking number not found");
      const response = await downloadTicketByBookingNo(bookingNo);
      if (!response) throw new Error("Failed to download ticket");
      const blob = new Blob([response], { type: "application/pdf" });
      window.open(window.URL.createObjectURL(blob), "_blank");
    } catch (err) {
      handleError(err);
    } finally {
      setDownloadingTicket(false);
    }
  };

  const moneyCell = (value: any, currency?: string) => (
    <span className="font-mono contact-sub-text fs-12px">
      {value ?? 0}
      {currency ? ` ${currency}` : ""}
    </span>
  );

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "userDetails",
        header: "USER DETAILS",
        accessorFn: (row) =>
          `${row?.Contact?.firstName || ""} ${row?.Contact?.lastName || ""}`.trim() ||
          row?.Contact?.email ||
          "Guest",
        cell: ({ row }) => {
          const item = row.original;
          const name =
            `${item?.Contact?.firstName || ""} ${item?.Contact?.lastName || ""}`.trim() || "Guest";
          return (
            <div className="d-flex flex-column gap-1">
              <span className="fw-bold contact-name-text fs-13px">{name}</span>
              <span className="font-mono contact-sub-text fs-11px">{item?.Contact?.email || "—"}</span>
              <span className="font-mono contact-sub-text fs-11px">{item?.Contact?.phoneNo || "—"}</span>
              {item?.Venue?.venueName && (
                <span className="contact-sub-text fs-11px">{item.Venue.venueName}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "ticketType",
        header: "TICKET DETAILS",
        accessorFn: (row) => row?.TicketType?.ticketType || "N/A",
        cell: ({ row }) => (
          <span className="contact-sub-text fs-12px">{row.original?.TicketType?.ticketType || "N/A"}</span>
        ),
      },
      {
        accessorKey: "totalTicketCount",
        header: "TICKETS SOLD",
        cell: ({ row }) => (
          <span className="font-mono contact-name-text fs-12px">{row.original?.totalTicketCount ?? 0}</span>
        ),
      },
      {
        accessorKey: "totalTicketAmount",
        header: "TICKET AMOUNT",
        cell: ({ row }) => moneyCell(row.original?.totalTicketAmount, row.original?.currency),
      },
      {
        accessorKey: "discountAmount",
        header: "DISCOUNT",
        cell: ({ row }) => moneyCell(row.original?.discountAmount),
      },
      {
        accessorKey: "processingFee",
        header: "PROCESSING FEE",
        cell: ({ row }) => moneyCell(row.original?.processingFee, row.original?.currency),
      },
      {
        accessorKey: "organizerRevenue",
        header: "ORGANISER REVENUE",
        cell: ({ row }) => (
          <span className="font-mono contact-name-text fs-12px">
            {row.original?.organizerRevenue ?? 0} {row.original?.currency || ""}
          </span>
        ),
      },
      {
        accessorKey: "grandTotal",
        header: "GRAND TOTAL",
        cell: ({ row }) => (
          <span className="font-mono contact-name-text fs-12px">
            {row.original?.grandTotal ?? 0} {row.original?.currency || ""}
          </span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "CREATED AT",
        cell: ({ row }) =>
          row.original?.createdAt ? (
            <span className="font-mono contact-sub-text fs-12px">
              <FormattedDate dateString={row.original.createdAt} />
            </span>
          ) : (
            <span className="contact-sub-text fs-12px">N/A</span>
          ),
      },
      {
        id: "action",
        header: "ACTION",
        enableSorting: false,
        cell: ({ row }) =>
          row.original?.bookingStatus === "confirmed" ? (
            <button
              type="button"
              className="contact-action-btn"
              onClick={() => handleDownloadTicket(row.original)}
              disabled={downloadingTicket}
            >
              <Download size={14} /> Ticket
            </button>
          ) : (
            <span className="contact-sub-text fs-12px">—</span>
          ),
      },
    ],
    [downloadingTicket]
  );

  const statIcons = [Wallet, BadgePercent, Receipt];
  const statColors = ["pink", "orange", "blue"];

  return (
    <div className={`contact-list-container ${isNightMode ? "text-white" : "text-dark"}`}>
      <Background />

      <PageHeader
        title="Revenue Report"
        subtitle="Review confirmed booking revenue, fees, and discounts. Filter by venue, name, or date, then export."
        icon={BarChart3}
        isLoading={isLoading}
        actions={
          <ExportToExcel
            data={generateExportData(displayRows)}
            fileName="Revenue_Report"
            buttonText="Export (.CSV)"
          />
        }
      />

      <div className="contact-stats-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        {reports.map((report, idx) => {
          const Icon = statIcons[idx] || BarChart3;
          return (
            <div className="contact-stat-card" key={report.title}>
              <div>
                <div className="contact-stat-label">{report.title.toUpperCase()}</div>
                <div className="contact-stat-value">{isLoading ? "..." : report.description}</div>
              </div>
              <div className={`contact-stat-icon ${statColors[idx] || "pink"}`}>
                <Icon size={22} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="contact-filter-card attendee-report-filters">
        <div className="attendee-filter-grid">
          {eventVenues.length > 1 && (
            <div className="attendee-filter-field">
              <CommonSelect
                options={eventVenues}
                value={selectedVenue}
                onChange={(e) => onSelectedVenue(e.target.value)}
                label="Event Venue"
              />
            </div>
          )}

          <div className="attendee-filter-field position-relative">
            <label className="subuser-stat-label">Name</label>
            <Search className="attendee-filter-icon contact-sub-text" size={16} />
            <input
              type="text"
              className="contact-search-input attendee-filter-input"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="attendee-filter-field">
            <label className="subuser-stat-label">Start Date</label>
            <FlatPickr
              className="contact-search-input attendee-date-input"
              value={fromDate}
              onChange={([date]) => setFromDate(date)}
              options={{ dateFormat: "Y-m-d" }}
              placeholder="Select start date"
            />
          </div>

          <div className="attendee-filter-field">
            <label className="subuser-stat-label">End Date</label>
            <FlatPickr
              className="contact-search-input attendee-date-input"
              value={toDate}
              onChange={([date]) => setToDate(date)}
              options={{ dateFormat: "Y-m-d" }}
              placeholder="Select end date"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="d-flex justify-content-end mt-3">
            <button type="button" className="contact-action-btn" onClick={clearFilters}>
              <RotateCcw size={14} /> Clear Filters
            </button>
          </div>
        )}
      </div>

      {downloadingTicket && (
        <div className="d-flex align-items-center justify-content-center py-2 gap-2">
          <PulseLoader color="#EE3F7C" size={8} />
          <span className="contact-sub-text fs-12px">Preparing ticket PDF…</span>
        </div>
      )}

      <TablesV2
        data={displayRows}
        columns={columns}
        isLoading={isLoading}
        getRowId={(row, idx) => String(row.bookingId || row.bookingNo || `rev-${idx}`)}
        loadingMessage="Loading revenue report..."
        emptyMessage="No bookings match your current filters."
        pageSize={25}
      />
    </div>
  );
};

export default EventRevenueReport;
